import { useQuery } from "@tanstack/react-query";
import { WorkspaceAnalytics } from "@/types/workspace";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/hooks/use-auth";
import { getNotificationClaimId, isMetadataRecord } from "@/utils/notification.utils";
import { CLAIM_MODULE_VALUES, type ClaimModuleKey } from "@/constants/claim-options";
import { isModuleBlockedForUser } from "@/lib/claim-assignee-module-utils";
import type {
    ActivityEvent,
    ActivityEventType,
    Claim,
    ClaimAssigneeBlockedModulesEnum,
} from "@/api/generated/client/models";

const CLAIM_FETCH_CONCURRENCY = 8;

const ACTIVITY_TYPE_TO_MODULE: Partial<Record<ActivityEventType, ClaimAssigneeBlockedModulesEnum>> = {
    task_created: "tasks",
    task_completed: "tasks",
    task_assigned: "tasks",
    document_uploaded: "documents",
    file_uploaded: "documents",
    comparison_run: "comparisons",
    comparison_completed: "comparisons",
    comparison_failed: "comparisons",
    supplement_created: "supplements",
    supplement_completed: "supplements",
    supplement_failed: "supplements",
    supplement_justification_updated: "supplements",
    note_created: "notes",
    comment_added: "notes",
    email_sent: "emails",
    email_received: "emails",
};

function moduleFromActivityMetadata(activity: ActivityEvent): ClaimAssigneeBlockedModulesEnum | null {
    const metadata = activity.metadata;
    if (!isMetadataRecord(metadata)) {
        return null;
    }

    const candidate = metadata.module ?? metadata.moduleKey;
    if (typeof candidate !== "string") {
        return null;
    }

    if (!CLAIM_MODULE_VALUES.has(candidate as ClaimModuleKey)) {
        return null;
    }

    return candidate as ClaimAssigneeBlockedModulesEnum;
}

function moduleForActivity(activity: ActivityEvent): ClaimAssigneeBlockedModulesEnum | null {
    const mappedFromType = ACTIVITY_TYPE_TO_MODULE[activity.type];
    if (mappedFromType) {
        return mappedFromType;
    }

    return moduleFromActivityMetadata(activity);
}

async function fetchClaimsById(claimIds: number[]): Promise<Map<number, Claim>> {
    const claimsById = new Map<number, Claim>();

    for (let index = 0; index < claimIds.length; index += CLAIM_FETCH_CONCURRENCY) {
        const batch = claimIds.slice(index, index + CLAIM_FETCH_CONCURRENCY);
        const batchResults = await Promise.all(
            batch.map(async (claimId) => {
                try {
                    const claim = await apiClient.claims.getClaim({ claimId });
                    return { claimId, claim };
                } catch {
                    // Fail closed in caller by leaving missing claimIds unresolved.
                    return { claimId, claim: null };
                }
            }),
        );

        for (const result of batchResults) {
            if (result.claim) {
                claimsById.set(result.claimId, result.claim);
            }
        }
    }

    return claimsById;
}

export function useWorkspaceAnalytics() {
    const { user } = useAuth();
    const currentUserId = user?.id;

    const { data, isLoading, error, refetch } = useQuery<{ success: boolean; data: WorkspaceAnalytics }>({
        queryKey: ['workspace-analytics', currentUserId],
        queryFn: async () => {
            const response = await apiClient.analytics.getWorkspaceAnalytics() as unknown as {
                success: boolean;
                data: WorkspaceAnalytics;
            };

            if (!currentUserId) {
                return response;
            }

            const targetClaimIds = new Set<number>();
            for (const task of response.data.upcomingTasks ?? []) {
                if (task.claimId) {
                    targetClaimIds.add(task.claimId);
                }
            }
            for (const activity of response.data.recentActivities ?? []) {
                const module = moduleForActivity(activity);
                if (!module) {
                    continue;
                }

                const claimId = getNotificationClaimId(activity) ?? activity.claimId ?? null;
                if (claimId) {
                    targetClaimIds.add(claimId);
                }
            }

            if (targetClaimIds.size === 0) {
                return response;
            }

            const claimsById = await fetchClaimsById(Array.from(targetClaimIds));

            const filteredUpcomingTasks = response.data.upcomingTasks.filter((task) => {
                const claim = claimsById.get(task.claimId);
                if (!claim) {
                    return false;
                }

                return !isModuleBlockedForUser(claim, currentUserId, "tasks");
            });

            const filteredRecentActivities = response.data.recentActivities.filter((activity) => {
                const module = moduleForActivity(activity);
                if (!module) {
                    return true;
                }

                const claimId = getNotificationClaimId(activity) ?? activity.claimId ?? null;
                if (!claimId) {
                    return true;
                }

                const claim = claimsById.get(claimId);
                if (!claim) {
                    return false;
                }

                return !isModuleBlockedForUser(claim, currentUserId, module);
            });

            return {
                ...response,
                data: {
                    ...response.data,
                    upcomingTasks: filteredUpcomingTasks,
                    recentActivities: filteredRecentActivities,
                },
            };
        },
        staleTime: 30 * 1000, // 30 seconds - shorter to pick up recent changes faster
        refetchOnWindowFocus: true, // Refetch when user returns to the tab
        refetchInterval: 60 * 1000, // Auto-refetch every 60 seconds for near real-time updates
        enabled: !!currentUserId,
    });

    return { 
        analytics: data?.data, // Backend returns EMPTY_WORKSPACE_ANALYTICS if no claims; undefined during loading
        isLoading,
        error,
        refetch, // Export refetch so components can manually refresh
    };
}
